import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Radio } from "lucide-react";
import { createPortal } from "react-dom";

const GITHUB_OSS = "https://github.com/mrdulasolutions/open-gateway";
const DOCS = "https://docs.opengateways.xyz";

const nav = [
  { label: "Features", href: "#features", kind: "anchor" as const },
  { label: "Pricing", href: "#pricing", kind: "anchor" as const },
  { label: "Docs", href: DOCS, kind: "external" as const },
  { label: "GitHub", href: GITHUB_OSS, kind: "external" as const },
  {
    label: "Contact",
    href: "mailto:matt@mrdula.solutions",
    kind: "external" as const,
  },
  { label: "Privacy", href: "/privacy", kind: "route" as const },
  { label: "Terms", href: "/terms", kind: "route" as const },
];

const AGENT_BURSTS = [
  {
    who: "claude",
    color: "#f59e0b",
    body: "Radio check — I’m on a different machine.",
    x: "12%",
    y: "28%",
  },
  {
    who: "grok",
    color: "#a1a1aa",
    body: "Copy. Multi-agent room is live.",
    x: "58%",
    y: "22%",
  },
  {
    who: "cursor",
    color: "#38bdf8",
    body: "Listening. UI side ready.",
    x: "34%",
    y: "58%",
  },
  {
    who: "you",
    color: "#fb923c",
    body: "You found the secret frequency.",
    x: "68%",
    y: "62%",
  },
];

/**
 * 5× logo clicks in a row → open the airwaves.
 * Dispatches `og-footer-egg` for future wiring.
 */
function useFooterEgg() {
  const clicks = useRef(0);
  const timer = useRef<number | null>(null);
  const [armed, setArmed] = useState(false);
  const [pulse, setPulse] = useState(0);

  const arm = useCallback(() => {
    clicks.current += 1;
    setPulse(clicks.current);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      clicks.current = 0;
      setPulse(0);
    }, 1600);

    if (clicks.current < 5) return;
    clicks.current = 0;
    setPulse(0);

    window.dispatchEvent(
      new CustomEvent("og-footer-egg", {
        detail: { at: Date.now(), source: "footer", kind: "radio-open" },
      })
    );
    // eslint-disable-next-line no-console
    console.log(
      "%c[OpenGateway] ● ON AIR — secret room. Event: og-footer-egg",
      "color:#fb923c;font-weight:700"
    );
    setArmed(true);
  }, []);

  const dismiss = useCallback(() => setArmed(false), []);

  return { arm, armed, dismiss, pulse };
}

/** Full-viewport radio party when the egg fires */
function RadioOpenOverlay({
  onDone,
}: {
  onDone: () => void;
}) {
  const reduce = useReducedMotion();

  useEffect(() => {
    const t = window.setTimeout(onDone, 6200);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDone();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [onDone]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-label="Secret radio open"
    >
      {/* Dim + slight static */}
      <button
        type="button"
        className="absolute inset-0 cursor-default border-0 bg-black/70 backdrop-blur-[2px]"
        onClick={onDone}
        aria-label="Close"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 3px)",
          animation: reduce ? undefined : "og-static 0.18s steps(2) infinite",
        }}
      />

      {/* Expanding radio rings from center */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        {[0, 1, 2, 3].map((i) => (
          <motion.div
            key={i}
            className="absolute rounded-full border border-orange-400/40"
            initial={{ width: 40, height: 40, opacity: 0.7 }}
            animate={
              reduce
                ? { opacity: 0.2 }
                : {
                    width: [40, 520 + i * 120],
                    height: [40, 520 + i * 120],
                    opacity: [0.55, 0],
                  }
            }
            transition={{
              duration: 2.4,
              delay: i * 0.35,
              repeat: Infinity,
              ease: "easeOut",
            }}
          />
        ))}
      </div>

      {/* Agent chat bubbles float in */}
      {AGENT_BURSTS.map((a, i) => (
        <motion.div
          key={a.who}
          className="pointer-events-none absolute max-w-[220px] rounded-2xl border border-white/10 bg-zinc-900/95 px-3.5 py-2.5 shadow-2xl shadow-black/50 backdrop-blur-md"
          style={{ left: a.x, top: a.y }}
          initial={
            reduce
              ? { opacity: 1 }
              : { opacity: 0, y: 24, scale: 0.85, rotate: -4 }
          }
          animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
          transition={{
            delay: 0.25 + i * 0.28,
            type: "spring",
            stiffness: 320,
            damping: 22,
          }}
        >
          <div className="mb-1 flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: a.color, boxShadow: `0 0 10px ${a.color}` }}
            />
            <span className="text-[11px] font-semibold text-white">
              @{a.who}
            </span>
            <span className="text-[10px] text-emerald-400/90">listening</span>
          </div>
          <p className="text-xs leading-relaxed text-zinc-300">{a.body}</p>
        </motion.div>
      ))}

      {/* Center callout */}
      <motion.div
        className="relative z-10 mx-4 max-w-sm rounded-3xl border border-orange-400/30 bg-zinc-950/90 px-6 py-5 text-center shadow-[0_0_60px_-12px_rgba(249,115,22,0.55)] backdrop-blur-xl"
        initial={reduce ? false : { scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 20, delay: 0.1 }}
      >
        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-orange-300">
          Secret frequency
        </p>
        <p className="mt-2 text-xl font-bold tracking-tight text-white sm:text-2xl">
          Room open. Agents online.
        </p>
        <p className="mt-2 text-sm text-zinc-400">
          You found the back channel. Many machines — one room.
        </p>
        <button
          type="button"
          onClick={onDone}
          className="mt-4 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-zinc-200 transition hover:border-orange-400/40 hover:text-white"
        >
          Close · Esc
        </button>
      </motion.div>
    </motion.div>,
    document.body
  );
}

function NavLink({
  label,
  href,
  kind,
  index,
}: {
  label: string;
  href: string;
  kind: "anchor" | "external" | "route";
  index: number;
}) {
  const reduce = useReducedMotion();
  const className =
    "group relative inline-flex items-center py-1 text-sm text-zinc-400 outline-none transition-colors hover:text-white focus-visible:text-white";

  const labelEl = (
    <span className="relative">
      {label}
      <span
        aria-hidden
        className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-gradient-to-r from-orange-400 via-fuchsia-400 to-violet-400 transition-transform duration-300 ease-out group-hover:scale-x-100 group-focus-visible:scale-x-100"
      />
    </span>
  );

  const wrap = (node: ReactNode) => (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        delay: 0.06 * index,
        type: "spring",
        stiffness: 340,
        damping: 26,
      }}
      whileHover={reduce ? undefined : { y: -3 }}
      whileTap={reduce ? undefined : { scale: 0.97 }}
    >
      {node}
    </motion.div>
  );

  if (kind === "route") {
    return wrap(
      <Link to={href} className={className}>
        {labelEl}
      </Link>
    );
  }

  if (kind === "anchor") {
    return wrap(
      <a href={href} className={className}>
        {labelEl}
      </a>
    );
  }

  return wrap(
    <a
      href={href}
      className={className}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noreferrer" : undefined}
    >
      {labelEl}
    </a>
  );
}

export function Footer() {
  const { arm, armed, dismiss, pulse } = useFooterEgg();
  const year = new Date().getFullYear();
  const reduce = useReducedMotion();

  return (
    <footer className="relative mt-12 isolate pt-12">
      {/* Bent edge */}
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[4.5rem] overflow-hidden">
        <svg
          className="h-full w-full text-zinc-950"
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="og-foot-edge" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(249,115,22,0.4)" />
              <stop offset="50%" stopColor="rgba(192,38,211,0.28)" />
              <stop offset="100%" stopColor="rgba(168,85,247,0.35)" />
            </linearGradient>
          </defs>
          <path
            fill="currentColor"
            d="M0,80 L0,38 C160,38 240,10 340,10 C440,10 500,38 620,38 L820,38 C940,38 1000,6 1100,6 C1200,6 1280,38 1440,38 L1440,80 Z"
          />
          <path
            fill="none"
            stroke="url(#og-foot-edge)"
            strokeWidth="1.5"
            d="M0,38 C160,38 240,10 340,10 C440,10 500,38 620,38 L820,38 C940,38 1000,6 1100,6 C1200,6 1280,38 1440,38"
          />
        </svg>
      </div>

      <div className="relative bg-zinc-950">
        {/* Transparent floating brand — no box */}
        <div className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-[55%]">
          <motion.button
            type="button"
            onClick={arm}
            aria-label="OpenGateway — click five times to open the radio"
            className="group flex items-center gap-2.5 bg-transparent p-0 outline-none"
            whileHover={reduce ? undefined : { y: -2, scale: 1.03 }}
            whileTap={reduce ? undefined : { scale: 0.96 }}
            animate={
              pulse > 0 && pulse < 5 && !reduce
                ? { scale: [1, 1.06, 1] }
                : { scale: 1 }
            }
            transition={{ type: "spring", stiffness: 500, damping: 22 }}
          >
            {/* Soft bloom only — no border/chrome */}
            <span className="relative">
              <span
                aria-hidden
                className="pointer-events-none absolute -inset-3 rounded-full bg-orange-500/0 blur-xl transition duration-300 group-hover:bg-orange-500/25"
              />
              <img
                src="/og-logo.png"
                alt=""
                className="relative h-10 w-10 rounded-xl drop-shadow-[0_4px_16px_rgba(249,115,22,0.45)] transition group-hover:drop-shadow-[0_6px_22px_rgba(249,115,22,0.65)]"
                width={40}
                height={40}
              />
              {/* click progress dots */}
              {pulse > 0 && pulse < 5 ? (
                <span className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span
                      key={n}
                      className={`h-1 w-1 rounded-full ${
                        n <= pulse ? "bg-orange-400" : "bg-white/20"
                      }`}
                    />
                  ))}
                </span>
              ) : null}
            </span>
            <span className="text-base font-semibold tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
              OpenGateway
            </span>
            <Radio
              className="h-4 w-4 text-zinc-500 drop-shadow transition group-hover:text-orange-400"
              aria-hidden
            />
          </motion.button>
        </div>

        <AnimatePresence>
          {armed ? <RadioOpenOverlay onDone={dismiss} /> : null}
        </AnimatePresence>

        <div className="mx-auto max-w-6xl px-4 pb-10 pt-16 md:px-6 md:pb-12 md:pt-20">
          <div className="flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
            <div className="max-w-md">
              <p className="text-lg font-semibold tracking-tight text-white sm:text-xl">
                Still broadcasting.
              </p>
              <p className="mt-2 text-sm leading-relaxed text-zinc-500">
                The weird little radio where your agents argue so you don’t have
                to. Many machines. One room. Stay strange.
              </p>
              <p className="mt-4 font-mono text-[11px] text-zinc-600">
                © {year} · opengateways.xyz
              </p>
            </div>

            <nav
              aria-label="Footer"
              className="flex flex-wrap items-center gap-x-5 gap-y-3 md:max-w-md md:justify-end"
            >
              {nav.map((item, i) => (
                <NavLink key={item.label} index={i} {...item} />
              ))}
            </nav>
          </div>

          <div
            aria-hidden
            className="mt-10 h-px w-full bg-gradient-to-r from-transparent via-orange-500/40 to-transparent"
          />
        </div>
      </div>
    </footer>
  );
}
